import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { dismissToast } from '@rsos/components/capstone/base/Toast';
import {
  ACCEPTED,
  TIMEOUT,
  DISPATCH_REQUESTED,
} from '@rsos/constants/alertStatuses';
import { NOTIFIED_AGENCY } from '@rsos/constants/alertsRoutingConstants';
import {
  addActiveAlertToasts,
  removeActiveAlertToasts,
} from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { activeToastAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { notifyGenericAlert } from '@rsos/utils/toastUtils';
import usePrevious from '@rsos/utils/usePrevious';
import {
  handleToastStatusChange,
  mockAlertToastsSettings,
  verifyEmergencyType,
} from './utils';

const useAllAlertsToastsNotifications = () => {
  const dispatch = useDispatch();

  const currentOrg = useSelector(state => state.sinatra.user.currentOrg);
  const applicationName = useSelector(
    state => state.sinatra.user.currentRole.application,
  );
  const profile = useSelector(state => state.sinatra.user.profile);
  const orgID = useMemo(() => currentOrg?.id, [currentOrg]);
  const IDWithNewAlert = useSelector(state => state.incidents.IDWithNewAlert);
  const activeAlertToasts = useSelector(
    state => state.incidents.activeAlertToasts,
  );

  const alerts = useSelector(state => state.incidents.alerts);
  const subsetAlertsFromToasts = useSelector(state =>
    activeToastAlertsSelector(state.incidents),
  );
  const removedAlerts = useSelector(state => state.incidents.removedAlerts);

  const resolvedAlertToastSettings = useSelector(
    state =>
      state.sinatra.user.profile?.organizations?.find(o => o.id === orgID)
        ?.attributes[applicationName]?.alert_toasts || mockAlertToastsSettings,
  );

  const prevSubsetAlertsFromToasts = usePrevious(subsetAlertsFromToasts) || {};

  const cleanUpToasts = alertID => dispatch(removeActiveAlertToasts(alertID));

  /** when a new alert comes in, we add a new active alert toast after diffing with settings  */
  useEffect(() => {
    if (IDWithNewAlert) {
      const isToastAlreadyActive = !!activeAlertToasts.find(
        t => t.alertID === IDWithNewAlert,
      );
      const alert = alerts[IDWithNewAlert];
      /** the condition below checks if the alert is in Dispatch Requested,
       * it is not a duplicate toast and
       * it corresponds to user settings by mapping its emergency type to the relevant alert type (andromeda capability) */
      if (
        !isToastAlreadyActive &&
        verifyEmergencyType(alert, resolvedAlertToastSettings) &&
        alert.status.name === DISPATCH_REQUESTED
      ) {
        const toastParams = {
          alert: alert,
          options: {
            onClose: () => cleanUpToasts(IDWithNewAlert),
          },
        };
        const id = notifyGenericAlert(toastParams);
        dispatch(
          addActiveAlertToasts({
            toastID: id,
            alertID: alert.alert_id,
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IDWithNewAlert]);

  /** use case when we need to update active toast when an alert is accepted */
  useEffect(() => {
    if (activeAlertToasts.length) {
      activeAlertToasts.forEach(t => {
        const { alertID, toastID } = t;
        const currentAlert = subsetAlertsFromToasts[alertID];
        const prevAlert = prevSubsetAlertsFromToasts[alertID];

        if (
          currentAlert?.associated_role_type !== NOTIFIED_AGENCY &&
          currentAlert?.status.name === ACCEPTED &&
          prevAlert?.status.name === DISPATCH_REQUESTED
        ) {
          handleToastStatusChange(ACCEPTED, currentAlert, toastID, profile);
        } else if (currentAlert?.status?.name === TIMEOUT) {
          dismissToast(toastID);
          cleanUpToasts(alertID);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsetAlertsFromToasts, cleanUpToasts]);

  /** use case when decline an alert */
  useEffect(() => {
    if (removedAlerts.length && activeAlertToasts.length) {
      activeAlertToasts.forEach(t => {
        const { alertID, toastID } = t;
        if (removedAlerts.find(element => element === alertID)) {
          dismissToast(toastID);
          cleanUpToasts(alertID);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removedAlerts]);
};

export default useAllAlertsToastsNotifications;
